import React, { useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import cn from 'classnames'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { resolveLink } from '../../helpers/resolveLink'
// Import our custom recipe slider components
import RecipeSlider from '../RecipeSlider/RecipeSlider'
import RecipeTile from '../RecipeSlider/RecipeTile'

const RecipeSliderSlice = ({ className, slice }) => {
  if (!slice) {
    console.error('RecipeSliderSlice: No slice data provided')
    return null
  }

  // Add defensive coding to handle different data structures
  // // console.log('RecipeSliderSlice raw data received:', slice)
  // // console.log('Slice properties:', Object.keys(slice))
  // // console.log('Has recipeRefs:', !!slice.recipeRefs)
  // // console.log('RecipeRefs type:', typeof slice.recipeRefs)
  // if (slice.recipeRefs) {
  //   // console.log('RecipeRefs is array:', Array.isArray(slice.recipeRefs))
  //   // console.log('RecipeRefs content:', slice.recipeRefs)
  // }

  // For data from Sanity with type property
  const isSanityFormat = slice.type === 'recipeSlider' || slice._type === 'recipeSlider'
  if (isSanityFormat) {
    // // console.log('Found Sanity recipeSlider format data:', slice.id || slice._id || 'unknown id')
  }

  // Get title with fallbacks - extract from different possible locations
  const title = get(slice, 'title') ||
               get(slice, 'name') ||
               (isSanityFormat ? 'Recipes from Discovered Foods' : 'Recipes')

  // Get description from slice
  const description = get(slice, 'description') ||
                     get(slice, 'subtitle') ||
                     get(slice, 'text') ||
                     null
  // Handle slides from different properties - based on your Sanity structure
  let slides = get(slice, 'slides', [])

  // Log the current slide count if present
  if (slides && Array.isArray(slides)) {
    // // console.log('Found slides in main slides array:', slides.length)
  }

  // If no slides found directly, check for other possible properties
  if (isEmpty(slides)) {
    // Check recipeRefs first (new field added to schema)
    if (slice.recipeRefs && Array.isArray(slice.recipeRefs)) {
      // // console.log('Found recipe references:', slice.recipeRefs.length)
      // // console.log('Raw recipeRefs data:', slice.recipeRefs)

      slides = slice.recipeRefs.map((ref, index) => {
        // Try to extract hero image from slices (same way as pageSummaryProjection)
        let heroImage = null
        if (ref.slices && Array.isArray(ref.slices)) {
          const heroSlice = ref.slices.find(s => s._type === 'page_hero' || s.type === 'page_hero' || s._type === 'homepage_hero_slice')
          if (heroSlice && heroSlice.image) {
            // Format the image the same way as pageSummaryProjection
            heroImage = {
              _type: heroSlice.image._type,
              asset: heroSlice.image.asset
            }
          }
        } else {
        }

        // Check all possible image sources
        // const imageOptions = {
        //   heroImage,
        //   refImage: ref.image,
        //   refMainImage: ref.mainImage,
        //   refHeroImage: ref.hero_image,
        //   refPreviewImage: ref.previewImage,
        //   refFeaturedImage: ref.featuredImage
        // }
        // // console.log('Available image options for ${ref.title}:', imageOptions)

        // Select the first available image - prioritize the formatted hero image
        const selectedImage = heroImage ||
                             ref.image ||
                             ref.mainImage ||
                             ref.hero_image ||
                             ref.previewImage ||
                             ref.featuredImage ||
                             null // Use null instead of placeholder object

        const slideData = {
          title: ref.title || 'Recipe',
          image: selectedImage,
          subtitle: ref.description || ref.subtitle || '',
          link: ref.slug ? {
            url: `/${ref.slug}`
          } : null
        }

        return slideData
      })
      // // console.log('Final slides array from recipeRefs:', slides)
    } else if (slice.recipes && Array.isArray(slice.recipes)) {
      // Check recipes array
      slides = slice.recipes
      // // console.log('Found slides in recipes property:', slides.length)
    } else if (slice.items && Array.isArray(slice.items)) {
      // Check items array
      slides = slice.items
      // console.log('Found slides in items property:', slides.length)
    } else if (slice.type === 'recipeSlider' && slice.link && Array.isArray(slice.link) && slice.link.length > 0) {
      // Special case for Sanity data that has link but no slides array
      // // console.log('Creating slides from Sanity link data:', slice.link)

      // Create a slide from the link reference data
      slides = slice.link.map(linkItem => {
        // Handle different link structures - sometimes the reference is directly in linkItem,
        // sometimes it's in a reference property
        const reference = linkItem.reference || linkItem
        if (!reference) return null

        // // console.log('Processing link item for slide:', reference)

        // Get page data from reference
        const pageData = reference

        // Log all available properties on the reference object to help with debugging
        // // console.log('Reference properties:', Object.keys(pageData))

        // Extract images if available - prioritize page_hero image from slices
        let mainImage = null

        if (pageData.slices && Array.isArray(pageData.slices)) {
          const heroSlice = pageData.slices.find(slice => slice._type === 'page_hero')
          if (heroSlice && heroSlice.image) {
            mainImage = heroSlice.image
            // // console.log('Found hero image in page_hero slice:', mainImage)
          }
        }

        // Fallback to direct image properties if no hero slice found
        if (!mainImage) {
          mainImage = pageData.mainImage ||
                     pageData.image ||
                     (pageData.sections && pageData.sections[0] && pageData.sections[0].image) ||
                     null
        }

        // Extract description if available
        const description = pageData.description ||
                          pageData.subtitle ||
                          pageData.excerpt ||
                          ''

        // console.log('Extracted image:', mainImage)

        return {
          title: pageData.title || 'Recipe',
          // Use reference data to create a slide - try multiple potential paths to image data
          image: mainImage,
          subtitle: description,
          // Create proper link format for recipe pages using the slug
          link: pageData.slug && pageData.slug.current
            ? `/${pageData.slug.current}`
            : (pageData.slug
              ? `/${pageData.slug}`
              : (linkItem._type === 'internal_link' ? linkItem : { _type: 'internal_link', reference: linkItem }))
        }
      }).filter(Boolean)

      // If we still couldn't create slides, create one default slide with the recipe title
      if (slides.length === 0 && slice.title) {
        slides = [{
          title: slice.title,
          subtitle: 'Recipe from Discovered Foods',
          url: '#',
          // Use a placeholder image
          image: {
            asset: {
              _ref: 'image-placeholder'
            }
          }
        }]
      }

      // // console.log('Created slides from link references:', slides)
    }
  }

  const size = get(slice, 'size', 'normal')
  const link = get(slice, 'link')

  // Debug what data we received
  // // console.log('RecipeSliderSlice processed:', {
  //   hasTitle: !!title,
  //   slidesCount: slides?.length,
  //   sliceKeys: Object.keys(slice)
  // })

  const classes = useStyles()

  const recipeTiles = useMemo(() => {
    if (isEmpty(slides)) {
      // console.warn('RecipeSliderSlice: No slides found')
      return []
    }

    try {
      // Validate slides array and filter out invalid entries
      const validSlides = slides.filter(slide => {
        if (!slide) {
          console.warn('RecipeSliderSlice: Found null/undefined slide, skipping')
          return false
        }

        // Check for required fields
        const hasTitle = get(slide, 'title') || get(slide, 'name')
        if (!hasTitle) {
          console.warn('RecipeSliderSlice: Slide missing title, skipping:', slide)
          return false
        }

        return true
      })

      if (validSlides.length === 0) {
        console.warn('RecipeSliderSlice: No valid slides after filtering')
        return []
      }

      return validSlides.map((slide, index) => {
        try {
          // // console.log('Processing slide:', slide)

          // Extract slide data with more flexible property names for Sanity data
          const slideTitle = get(slide, 'title') || get(slide, 'name') || 'Untitled Recipe'
          const slideImage = get(slide, 'image') || get(slide, 'mainImage')
          const slideSubtitle = get(slide, 'subtitle') ||
                               (get(slide, 'description') ? String(get(slide, 'description')).substring(0, 150) : '') ||
                               (get(slide, 'excerpt') ? String(get(slide, 'excerpt')).substring(0, 150) : '')

          // Handle different link structures - could be direct or nested
          const slideLink = get(slide, 'link') || get(slide, 'url') || get(slide, 'slug') || slide

          // Debug individual slides
          // // console.log('Recipe slide data processed:', {
          //   slideTitle,
          //   hasImage: !!slideImage,
          //   hasLink: !!slideLink,
          //   slideKeys: slide ? Object.keys(slide) : []
          // })

          // Safety check for expected data structures
          let resolvedLink
          try {
            if (Array.isArray(slideLink)) {
              resolvedLink = resolveLink(get(slideLink, [0]))
            } else if (typeof slideLink === 'object' && slideLink !== null) {
              // Handle various link formats
              if (slideLink.slug && (slideLink.slug.current || slideLink.slug.slug)) {
                // Format: { slug: { current: 'path' } } or { slug: { slug: 'path' } }
                const slugPath = slideLink.slug.current || slideLink.slug.slug || slideLink.slug
                resolvedLink = `/${slugPath}`
              } else if (slideLink._type === 'internal_link' || slideLink.type === 'internalLink') {
                // It's an internal link object
                resolvedLink = resolveLink(slideLink)
              } else {
                // Try to use the link resolver
                resolvedLink = resolveLink(slideLink)
              }
            } else if (slideLink && typeof slideLink === 'string') {
              // Direct URL - could be a slug path
              resolvedLink = slideLink.startsWith('/') || slideLink.startsWith('http')
                ? slideLink
                : `/${slideLink}`
            } else {
              resolvedLink = '#'
            }
          } catch (err) {
            console.error('Error resolving link:', err, slideLink)
            resolvedLink = '#'
          }

          // // console.log('Resolved link:', resolvedLink)

          // Ensure image is properly formatted for the Tile component
          let formattedImage = slideImage

          // // console.log('Processing image for tile:', slideTitle, slideImage)

          // Enhanced image handling with better fallbacks and direct URL support
          if (slideImage && slideImage.asset && slideImage.asset._ref) {
            // For Sanity images with _ref, keep the format as-is
            // console.log('Using Sanity image format with asset._ref', slideImage.asset._ref)
            formattedImage = slideImage
          } else if (slideImage && slideImage.src) {
            // Handle image object with src property
            // console.log('Using image with src property:', slideImage.src)
            formattedImage = {
              ...slideImage,
              // Add sizes array if not present for ResponsiveImage component
              sizes: slideImage.sizes || [{
                url: slideImage.src,
                width: 800
              }]
            }
          } else if (slideImage && typeof slideImage === 'string') {
            // Handle URL-based image format - create both src and sizes for maximum compatibility
            const imageUrl = slideImage.startsWith('http') ? slideImage : `https:${slideImage}`
            // console.log('Converting string URL to image object:', imageUrl)
            formattedImage = {
              src: imageUrl,
              alt: slideTitle || 'Recipe image',
              sizes: [{
                url: imageUrl,
                width: 800
              }]
            }
          } else if (slideImage && slideImage.url) {
            // Handle case with just a URL property
            const imageUrl = slideImage.url.startsWith('http') ? slideImage.url : `https:${slideImage.url}`
            // console.log('Using image with url property:', imageUrl)
            formattedImage = {
              src: imageUrl,
              alt: slideTitle || 'Recipe image',
              sizes: [{
                url: imageUrl,
                width: 800
              }]
            }
          } else if (!slideImage) {
            // Handle empty image case
            // console.log('No image found, using placeholder')
            formattedImage = {
              src: '/placeholder.jpg',
              alt: 'Placeholder image'
            }
          } else {
            // Unknown format - try to use as is but add direct src property as fallback
            // console.log('Unknown image format:', typeof slideImage, slideImage)
            formattedImage = {
              ...slideImage,
              src: '/placeholder.jpg', // Fallback src
              alt: slideTitle || 'Recipe image'
            }
          }

          // Final check to ensure we have some image data
          if (!formattedImage.src && !formattedImage.asset && (!formattedImage.sizes || formattedImage.sizes.length === 0)) {
            // console.log('Failed to create valid image format, using placeholder')
            formattedImage = {
              src: '/placeholder.jpg',
              alt: 'Placeholder image'
            }
          }
          return {
            title: slideTitle,
            subtitle: slideSubtitle,
            image: formattedImage,
            url: resolvedLink || '#'
          }
        } catch (error) {
          console.error(`RecipeSliderSlice: Error processing slide ${index}:`, error, slide)
          // Return a fallback slide object
          return {
            title: 'Recipe',
            subtitle: 'Error loading recipe details',
            image: { src: '/placeholder.jpg', alt: 'Placeholder image' },
            url: '#'
          }
        }
      }).filter(Boolean) // Filter out any null results
    } catch (error) {
      console.error('RecipeSliderSlice: Error processing slides:', error)
      return []
    }
  }, [slides])

  // // console.log('Final recipe tiles to display:', recipeTiles.length)

  // Resolve the link with fallbacks
  let resolvedHeaderLink
  try {
    resolvedHeaderLink = link && Array.isArray(link)
      ? resolveLink(get(link, [0]))
      : (link ? resolveLink(link) : null)
  } catch (err) {
    console.warn('Could not resolve header link:', err)
    resolvedHeaderLink = null
  }
  // If no slides are available, show a simple message
  if (isEmpty(recipeTiles)) {
    return (
      <div className={cn(className, classes.section, classes.noSlides)}>
        <h2>{title}</h2>
        <p className={classes.noSlidesMessage}>No recipes available yet.</p>
      </div>
    )
  }

  return (
    <RecipeSlider
      className={cn(className, classes.section)}
      title={title}
      description={description}
      link={resolvedHeaderLink}
    >
      {recipeTiles.map(({ image, title, url }, i) => {
        // Debug the final image format being passed to the RecipeTile
        return (
          <RecipeTile
            key={i}
            image={image}
            title={title || 'Recipe'}
            link={url || '#'}
            size={size || 'normal'}
          />
        )
      })}
    </RecipeSlider>
  )
}

const useStyles = createUseStyles({
  section: {
    // Custom styles for recipe slider
    margin: '1rem 0 2rem',
    '& h2': {
      marginBottom: '1.5rem'
    },
    '& .flickity-viewport': {
      overflow: 'visible'
    }
  },
  // Add styles for horizontal title layout
  horizontalTitle: {
    display: 'block',
    marginBottom: '20px',
    position: 'relative',
    textAlign: 'left'
  },
  noSlides: {
    textAlign: 'center',
    padding: '2rem',
    '& h2': {
      marginBottom: '1rem',
      fontSize: '1.5rem'
    }
  },
  noSlidesMessage: {
    color: '#666',
    fontSize: '1rem',
    fontStyle: 'italic'
  }
}, { name: 'RecipeSliderSlice' })

export default RecipeSliderSlice
